import { Button, Card, Divider, Form, Input, InputNumber, Modal, Select, Space, Typography } from 'antd'
import { ArrowLeftOutlined, SaveOutlined } from '@ant-design/icons'
import SystemHeader from '@/widgets/layout/SystemHeader'
import { BackButton } from '@/widgets/demo/ListPrimitives'
import { useAppNavigate } from '../../hooks/useAppNavigate'
import { applyApiFieldErrors, isHandledAuthError, municipalitiesApi } from '@/services'
import { useToast } from '@/widgets/common'

const { Title, Text } = Typography

type MunicipalityCreateFormValues = {
  code: string
  name: string
  prefecture: string
  contact: string
  status: 'ACTIVE' | 'INACTIVE'
  ocrUse: boolean
  transferRequesterIdCode?: string
  transferRequesterName?: string
  transferBankCode?: string
  transferBankName?: string
  transferBranchCode?: string
  transferBranchName?: string
  transferAccountType?: 1 | 2
  transferAccountNumber?: string
  privacyPolicyUrl: string
  subdomain: string
  maxUserCount: number
}

const requiredLabel = (label: string) => (
  <span className="inline-flex items-center gap-1">
    <span aria-hidden className="text-sm font-bold text-red-500">
      *
    </span>
    <span>{label}</span>
  </span>
)

const MunicipalityCreate = () => {
  const navigate = useAppNavigate()
  const toast = useToast()
  const [form] = Form.useForm<MunicipalityCreateFormValues>()

  const register = async () => {
    let values: MunicipalityCreateFormValues
    try {
      values = await form.validateFields()
    } catch (validationError: unknown) {
      if (validationError && typeof validationError === 'object' && 'errorFields' in validationError) return
      Modal.error({ content: '入力内容を確認してください。' })
      return
    }

    try {
      await municipalitiesApi.create({
        code: values.code.trim(),
        name: values.name.trim(),
        prefecture: values.prefecture?.trim() || undefined,
        contact: values.contact?.trim() || undefined,
        ocrUse: values.ocrUse,
        status: values.status,
        transferRequesterIdCode: values.transferRequesterIdCode?.trim() || undefined,
        transferRequesterName: values.transferRequesterName?.trim() || undefined,
        transferBankCode: values.transferBankCode?.trim() || undefined,
        transferBankName: values.transferBankName?.trim() || undefined,
        transferBranchCode: values.transferBranchCode?.trim() || undefined,
        transferBranchName: values.transferBranchName?.trim() || undefined,        transferAccountType: values.transferAccountType,
        transferAccountNumber: values.transferAccountNumber?.trim() || undefined,
        privacyPolicyUrl: values.privacyPolicyUrl?.trim() || undefined,
        subdomain: values.subdomain.trim(),
        maxUserCount: values.maxUserCount,
      })
      toast.success('自治体を登録しました。')
      if (values.ocrUse) {
        navigate('municipality-ocr-mapping', { code: values.code.trim() })
      } else {
        navigate('municipality-management')
      }
    } catch (error) {
      if (isHandledAuthError(error)) return
      applyApiFieldErrors(form, error)
    }
  }

  return (
    <div className="min-h-screen bg-gray-100">
      <SystemHeader
        breadcrumbs={[
          { label: 'システム管理', page: 'submenu-admin' },
          { label: '自治体管理', page: 'municipality-management' },
          { label: '新規自治体登録' },
        ]}
      />
      <div className="mx-auto max-w-2xl px-6 py-8">
        <BackButton onClick={() => navigate('municipality-management')} />
        <Card>
          <Title level={4}>新規自治体登録</Title>
          <Text type="secondary">必須項目を入力し、「登録」を押してください。</Text>
          <Divider />

          <Form<MunicipalityCreateFormValues>
            form={form}
            layout="vertical"
            requiredMark={false}
            className="customer-validation-form"
            initialValues={{
              code: '',
              name: '',
              prefecture: '',
              contact: '',
              status: 'ACTIVE',
              ocrUse: false,
              transferRequesterIdCode: '',
              transferRequesterName: '',
              transferBankCode: '',
              transferBankName: '',
              transferBranchCode: '',
              transferBranchName: '',
              transferAccountType: 1,
              transferAccountNumber: '',
              privacyPolicyUrl: '',
              subdomain: '',
              maxUserCount: 9999,
            }}
          >
            <Form.Item
              name="code"
              label={requiredLabel('自治体コード')}
              rules={[
                { required: true, message: '自治体コードは必須です。' },
                { pattern: /^\d{6}$/, message: '自治体コードは6桁の数字で入力してください。' },
              ]}
              getValueFromEvent={(event) => String(event?.target?.value ?? '').replace(/\D/g, '').slice(0, 6)}
            >
              <Input inputMode="numeric" maxLength={6} style={{ fontFamily: 'monospace' }} placeholder="131016" />
            </Form.Item>

            <Form.Item
              name="name"
              label={requiredLabel('自治体名')}
              rules={[
                { required: true, message: '自治体名は必須です。' },
                { max: 128, message: '自治体名は128文字以内で入力してください。' },
              ]}
            >
              <Input maxLength={128} />
            </Form.Item>

            <Form.Item
              name="subdomain"
              label={requiredLabel('サブドメイン')}
              extra="非システム管理者は、このサブドメインで始まるホストでのみアクセスできます。"
              rules={[
                { required: true, message: 'サブドメインは必須です。' },
                { max: 1024, message: 'サブドメインは1024文字以内で入力してください。' },
                {
                  pattern: /^[A-Za-z0-9_-]+(\.[A-Za-z0-9_-]+)*$/,
                  message: '半角英数・ハイフン・アンダースコアで入力し、ドットを連続・先頭・末尾に置かないでください。',
                },
              ]}
              getValueFromEvent={(event) => String(event?.target?.value ?? '').trim().toLowerCase()}
            >
              <Input maxLength={1024} placeholder="tokyo" style={{ fontFamily: 'monospace' }} />
            </Form.Item>

            <Form.Item name="prefecture" label="都道府県" rules={[{ max: 64, message: '都道府県は64文字以内で入力してください。' }]}>
              <Input maxLength={64} />
            </Form.Item>

            <Form.Item name="contact" label="窓口連絡先" rules={[{ max: 32, message: '窓口連絡先は32文字以内で入力してください。' }]}>
              <Input maxLength={32} placeholder="03-1234-5678" />
            </Form.Item>

            <Form.Item
              name="maxUserCount"
              label={requiredLabel('利用ユーザー数（上限）')}
              rules={[
                { required: true, message: '利用ユーザー数（上限）は必須です' },
                { type: 'number', min: 1, message: '利用ユーザー数（上限）は1以上で入力してください' },
              ]}
            >
              <InputNumber min={1} precision={0} style={{ width: '100%' }} />
            </Form.Item>

            <Form.Item name="privacyPolicyUrl" label="プライバシーポリシーURL">
              <Input.TextArea autoSize={{ minRows: 1, maxRows: 4 }} placeholder="https://www.example.lg.jp/privacy" />
            </Form.Item>

            <Divider />
            <Title level={5} style={{ marginTop: 0 }}>振込依頼人・口座</Title>

            <Form.Item
              name="transferRequesterIdCode"
              label="振込依頼人識別コード"
              rules={[
                { pattern: /^\d{1,10}$/, message: '10桁以内の数字で入力してください。' },
              ]}
              getValueFromEvent={(event) => String(event?.target?.value ?? '').replace(/\D/g, '').slice(0, 10)}
            >
              <Input maxLength={10} inputMode="numeric" style={{ fontFamily: 'monospace' }} placeholder="0123456789" />
            </Form.Item>

            <Form.Item
              name="transferRequesterName"
              label="振込依頼人名"
              rules={[
                { max: 40, message: '振込依頼人名は40文字以内で入力してください。' },
              ]}
            >
              <Input maxLength={40} placeholder="ﾃﾞﾓｼｼﾔｸｼﾖ" />
            </Form.Item>

            <Form.Item
              name="transferBankCode"
              label="取引金融機関コード"
              rules={[
                { pattern: /^\d{4}$/, message: '4桁の数字で入力してください。' },
              ]}
              getValueFromEvent={(event) => String(event?.target?.value ?? '').replace(/\D/g, '').slice(0, 4)}
            >
              <Input maxLength={4} inputMode="numeric" style={{ fontFamily: 'monospace' }} placeholder="9901" />
            </Form.Item>

            <Form.Item
              name="transferBankName"
              label="取引金融機関名"
              rules={[
                { max: 15, message: '取引金融機関名は15文字以内で入力してください。' },
              ]}
            >
              <Input maxLength={15} placeholder="ﾃﾞﾓﾀﾞｲｲﾁ" />
            </Form.Item>

            <Form.Item
              name="transferBranchCode"
              label="取引支店番号"
              rules={[
                { pattern: /^\d{3}$/, message: '3桁の数字で入力してください。' },
              ]}
              getValueFromEvent={(event) => String(event?.target?.value ?? '').replace(/\D/g, '').slice(0, 3)}
            >
              <Input maxLength={3} inputMode="numeric" style={{ fontFamily: 'monospace' }} placeholder="015" />
            </Form.Item>

            <Form.Item
              name="transferBranchName"
              label="取引支店名"
              rules={[
                { max: 15, message: '取引支店名は15文字以内で入力してください。' },
              ]}
            >
              <Input maxLength={15} placeholder="ﾁﾕｳｵｳ" />
            </Form.Item>

            <Form.Item
              name="transferAccountType"
              label="振込依頼人の預金科目"
            >
              <Select
                options={[
                  { value: 1, label: '普通預金' },
                  { value: 2, label: '当座預金' },
                ]}
              />
            </Form.Item>

            <Form.Item
              name="transferAccountNumber"
              label="振込依頼人の口座番号"
              rules={[
                { pattern: /^\d{1,7}$/, message: '7桁以内の数字で入力してください。' },
              ]}
              getValueFromEvent={(event) => String(event?.target?.value ?? '').replace(/\D/g, '').slice(0, 7)}
            >
              <Input maxLength={7} inputMode="numeric" style={{ fontFamily: 'monospace' }} placeholder="0001234" />
            </Form.Item>

            <Divider />

            <Form.Item name="ocrUse" label={requiredLabel('OCR利用')}>
              <Select
                options={[
                  { value: false, label: '無' },
                  { value: true, label: '有' },
                ]}
              />
            </Form.Item>

            <Form.Item name="status" label="ステータス">
              <Select
                options={[
                  { value: 'ACTIVE', label: '有効' },
                  { value: 'INACTIVE', label: '無効' },
                ]}
              />
            </Form.Item>
          </Form>
        </Card>

        <div className="mt-4 flex justify-end">
          <Space>
            <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('municipality-management')}>
              キャンセル
            </Button>
            <Button type="primary" icon={<SaveOutlined />} onClick={() => void register()}>
              登録
            </Button>
          </Space>
        </div>
      </div>
    </div>
  )
}

export default MunicipalityCreate
